import type { GpbDefinition } from '../../types/gpb';

/**
 * ⭐⭐ **NOTHING READS A DEFINITION BEFORE ITS SHAPE IS KNOWN.**
 *
 * The authoring panel used to `JSON.parse(text) as GpbDefinition` — a cast, not a
 * check — and the validator and the preview then walked `rank.quests.length`,
 * `goal.all.map` and `p.occurrences.map` on whatever had arrived. A `ranks` that is
 * a string, a goal whose `all` is an object, a `count` of `"7"`: each either throws
 * inside a render or, worse, validates as something it is not (`"7" > 0` is true).
 * Drafts saved by another admin, or pasted by hand, reach the same code.
 *
 * So this runs FIRST, everywhere a definition comes from outside the bundle:
 *  - `inspectShape` reports every value whose TYPE is wrong, with its path. It
 *    does not judge content — an empty title is the validator's business, and a
 *    draft is allowed to be unfinished.
 *  - `normalizeDraft` fills the containers a draft may simply not have yet
 *    (`ranks: []`, an empty ability), so an incomplete draft can be edited.
 *
 * ⚠️⚠️ **LOSSLESS.** Neither function drops or rewrites a field it does not know.
 * A definition carries more than any editor shows (`ref`, `after`, `variant`,
 * `used`, fields added after this file was written); normalising by rebuilding
 * objects from a known field list would silently delete them on the next save.
 * Every object is spread, and only MISSING containers are added.
 */

export interface ShapeIssue {
  /** JSON-ish path: `ranks[2].quests[0].goals[1].all[0].count`. */
  where: string;
  message: string;
}

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);

const PREDICATE_KINDS = ['distinct_dates', 'occurrences', 'skills', 'tasks', 'record'] as const;

/** Field → expected type, per predicate kind. Optional unless listed in `required`. */
const PREDICATE_FIELDS: Record<string, { required: Record<string, string>; optional: Record<string, string> }> = {
  distinct_dates: { required: { habit: 'string', count: 'number', per: 'per' }, optional: {} },
  occurrences: {
    required: { program: 'string', ids: 'string[]', mode: 'mode' },
    optional: { ordered: 'boolean', variant: 'string', fresh: 'boolean' },
  },
  skills: { required: { ids: 'string[]' }, optional: { fresh: 'boolean' } },
  tasks: { required: { project: 'string' }, optional: { fresh: 'boolean' } },
  record: { required: { record: 'string', fields: 'string[]' }, optional: { used: 'boolean', fresh: 'boolean' } },
};

function typeOk(v: unknown, t: string): boolean {
  switch (t) {
    case 'string': return typeof v === 'string';
    case 'number': return typeof v === 'number' && Number.isFinite(v);
    case 'boolean': return typeof v === 'boolean';
    case 'string[]': return Array.isArray(v) && v.every(x => typeof x === 'string');
    case 'per': return v === 'day' || v === 'opportunity';
    case 'mode': return v === 'all' || v === 'any';
    default: return false;
  }
}

const describe = (t: string): string =>
  t === 'string[]' ? 'a list of text values'
    : t === 'per' ? '"day" or "opportunity"'
      : t === 'mode' ? '"all" or "any"'
        : t === 'number' ? 'a number' : t === 'boolean' ? 'true or false' : 'text';

/**
 * Every value whose type is wrong. Empty = safe to normalise, validate and render.
 *
 * ⚠️ Missing is not wrong: a draft may not have ranks yet. Only a PRESENT value of
 * the wrong type is reported, because that is what crashes or misreads.
 */
export function inspectShape(value: unknown): ShapeIssue[] {
  const out: ShapeIssue[] = [];
  if (!isObj(value)) {
    out.push({ where: '(root)', message: 'a profile definition must be a JSON object.' });
    return out;
  }
  const str = (o: Obj, k: string, at: string) => {
    if (k in o && o[k] !== undefined && typeof o[k] !== 'string') out.push({ where: `${at}.${k}`, message: 'must be text.' });
  };
  const num = (o: Obj, k: string, at: string) => {
    if (k in o && o[k] !== undefined && !typeOk(o[k], 'number')) out.push({ where: `${at}.${k}`, message: 'must be a number.' });
  };
  /** A present array of objects; returns the objects (possibly empty). */
  const list = (o: Obj, k: string, at: string): { item: Obj; at: string }[] => {
    if (!(k in o) || o[k] === undefined) return [];
    const v = o[k];
    if (!Array.isArray(v)) { out.push({ where: `${at}.${k}`, message: 'must be a list.' }); return []; }
    const items: { item: Obj; at: string }[] = [];
    v.forEach((x, i) => {
      if (isObj(x)) items.push({ item: x, at: `${at}.${k}[${i}]` });
      else out.push({ where: `${at}.${k}[${i}]`, message: 'must be an object.' });
    });
    return items;
  };
  const obj = (o: Obj, k: string, at: string): Obj | null => {
    if (!(k in o) || o[k] === undefined) return null;
    if (!isObj(o[k])) { out.push({ where: `${at}.${k}`, message: 'must be an object.' }); return null; }
    return o[k] as Obj;
  };
  const strings = (o: Obj, k: string, at: string) => {
    if (k in o && o[k] !== undefined && !typeOk(o[k], 'string[]')) out.push({ where: `${at}.${k}`, message: 'must be a list of text values.' });
  };

  const root = value;
  const R = '';
  for (const k of ['id', 'author', 'version', 'name', 'promise']) str(root, k, R);

  const release = obj(root, 'release', R);
  if (release) for (const k of ['version', 'publishedOn', 'summary', 'supersedes']) str(release, k, '.release');

  for (const { item: rank, at } of list(root, 'ranks', R)) {
    num(rank, 'rank', at); str(rank, 'title', at); str(rank, 'blurb', at);
    for (const { item: quest, at: qa } of list(rank, 'quests', at)) {
      for (const k of ['id', 'title', 'blurb']) str(quest, k, qa);
      for (const { item: goal, at: ga } of list(quest, 'goals', qa)) {
        for (const k of ['id', 'title', 'evidenceLine']) str(goal, k, ga);
        for (const { item: pred, at: pa } of list(goal, 'all', ga)) {
          const kind = pred.kind;
          if (typeof kind !== 'string' || !(PREDICATE_KINDS as readonly string[]).includes(kind)) {
            out.push({ where: `${pa}.kind`, message: `must be one of ${PREDICATE_KINDS.join(', ')}.` });
            continue;
          }
          str(pred, 'ref', pa); str(pred, 'after', pa);
          const spec = PREDICATE_FIELDS[kind];
          for (const [k, t] of Object.entries(spec.required)) {
            if (!typeOk(pred[k], t)) out.push({ where: `${pa}.${k}`, message: `${kind} needs ${k} as ${describe(t)}.` });
          }
          for (const [k, t] of Object.entries(spec.optional)) {
            if (k in pred && pred[k] !== undefined && !typeOk(pred[k], t)) out.push({ where: `${pa}.${k}`, message: `must be ${describe(t)}.` });
          }
        }
      }
    }
  }

  for (const { item: p, at } of list(root, 'projects', R)) {
    for (const k of ['id', 'title', 'domain']) str(p, k, at);
    for (const { item: t, at: ta } of list(p, 'tasks', at)) for (const k of ['id', 'title']) str(t, k, ta);
  }

  const comps = obj(root, 'components', R);
  if (comps) {
    const C = '.components';
    for (const { item: h, at } of list(comps, 'habits', C)) for (const k of ['id', 'title', 'domain', 'area', 'behaviour']) str(h, k, at);
    for (const { item: p, at } of list(comps, 'programs', C)) {
      for (const k of ['id', 'title', 'domain']) str(p, k, at);
      for (const { item: o, at: oa } of list(p, 'occurrences', at)) { str(o, 'id', oa); str(o, 'card', oa); num(o, 'day', oa); }
    }
    const ability = obj(comps, 'ability', C);
    if (ability) {
      for (const k of ['id', 'title', 'domain', 'description']) str(ability, k, `${C}.ability`);
      for (const { item: s, at } of list(ability, 'skills', `${C}.ability`)) {
        for (const k of ['id', 'title', 'tier', 'description']) str(s, k, at);
        for (const { item: r, at: ra } of list(s, 'requirements', at)) for (const k of ['id', 'title', 'type', 'description', 'url']) str(r, k, ra);
      }
    }
  }

  const loadout = obj(root, 'loadout', R);
  if (loadout) {
    str(loadout, 'dominant', '.loadout'); str(loadout, 'premise', '.loadout');
    for (const { item: d, at } of list(loadout, 'domains', '.loadout')) {
      str(d, 'domain', at); num(d, 'low', at); num(d, 'high', at); str(d, 'role', at); strings(d, 'carries', at);
    }
    const sleep = obj(loadout, 'sleep', '.loadout');
    if (sleep) { num(sleep, 'low', '.loadout.sleep'); num(sleep, 'high', '.loadout.sleep'); }
  }
  const day = obj(root, 'dayTemplate', R);
  if (day) {
    str(day, 'title', '.dayTemplate'); str(day, 'caveat', '.dayTemplate');
    for (const { item: b, at } of list(day, 'blocks', '.dayTemplate')) for (const k of ['from', 'to', 'label', 'domain']) str(b, k, at);
  }

  return out.map(i => ({ ...i, where: i.where.replace(/^\./, '') }));
}

/**
 * Fill the containers an unfinished draft may not have yet, keeping every field it
 * does have. ⚠️ Call only after `inspectShape` returned nothing.
 */
export function normalizeDraft(value: Obj): GpbDefinition {
  const v = value as Obj & Partial<Record<string, unknown>>;
  const comps = isObj(v.components) ? v.components : {};
  const ability = isObj(comps.ability) ? comps.ability : {};
  const ranks = Array.isArray(v.ranks) ? v.ranks : [];
  return {
    ...v,
    id: typeof v.id === 'string' ? v.id : '',
    author: typeof v.author === 'string' ? v.author : '',
    version: typeof v.version === 'string' ? v.version : '',
    name: typeof v.name === 'string' ? v.name : '',
    promise: typeof v.promise === 'string' ? v.promise : '',
    release: isObj(v.release) ? { version: '', publishedOn: '', summary: '', ...v.release } : { version: '', publishedOn: '', summary: '' },
    ranks: ranks.map((r: Obj) => ({
      ...r,
      quests: (Array.isArray(r.quests) ? r.quests : []).map((q: Obj) => ({
        ...q,
        goals: (Array.isArray(q.goals) ? q.goals : []).map((g: Obj) => ({ ...g, all: Array.isArray(g.all) ? g.all : [] })),
      })),
    })),
    projects: (Array.isArray(v.projects) ? v.projects : []).map((p: Obj) => ({ ...p, tasks: Array.isArray(p.tasks) ? p.tasks : [] })),
    components: {
      ...comps,
      habits: Array.isArray(comps.habits) ? comps.habits : [],
      programs: (Array.isArray(comps.programs) ? comps.programs : []).map((p: Obj) => ({
        ...p, occurrences: Array.isArray(p.occurrences) ? p.occurrences : [],
      })),
      ability: {
        id: '', title: '', domain: '', description: '',
        ...ability,
        skills: Array.isArray(ability.skills) ? ability.skills : [],
      },
    },
  } as unknown as GpbDefinition;
}

export type ParseResult =
  | { ok: true; def: GpbDefinition }
  | { ok: false; issues: readonly ShapeIssue[] };

/** Text → a definition the editor and validator can safely read, or every reason it cannot. */
export function parseDefinitionText(text: string): ParseResult {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (e) {
    return { ok: false, issues: [{ where: '(json)', message: e instanceof Error ? e.message : 'That is not valid JSON.' }] };
  }
  return readDefinition(value);
}

/** Any value (a parsed import, a stored draft) → a safe definition, or its shape issues. */
export function readDefinition(value: unknown): ParseResult {
  const issues = inspectShape(value);
  if (issues.length) return { ok: false, issues };
  return { ok: true, def: normalizeDraft(value as Obj) };
}
